import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';

// #1187 — Status-Pflicht-Pop-up nach externer Antwort
// Pre-Selection-Logik (siehe Notion-Spec):
//   waiting_for_support       → response_required
//   in_progress               → response_required
//   response_required         → in_progress
//   solution_proposed         → done
//   done                      → in_progress
//   clarification_with_recipient → in_progress
const PRESELECT_MAP = {
  waiting_for_support: 'response_required',
  in_progress: 'response_required',
  response_required: 'in_progress',
  solution_proposed: 'done',
  done: 'in_progress',
  clarification_with_recipient: 'in_progress',
};

const MODAL_STAGE_KEYS = ['in_progress', 'response_required', 'solution_proposed', 'done'];

export default function StatusAfterSendModal({ isOpen, currentStatus, stages, onSelect, onCancel }) {
  const { t, getStageLabel } = useLanguage();
  const options = useMemo(() => {
    const map = new Map((stages || []).map(s => [s.key, s]));
    return MODAL_STAGE_KEYS
      .map(k => map.get(k))
      .filter(Boolean)
      .map(s => ({ key: s.key, label: getStageLabel(s.name) || s.name }));
  }, [stages, getStageLabel]);

  const defaultKey = PRESELECT_MAP[currentStatus] || 'in_progress';
  const initialIdx = Math.max(0, options.findIndex(o => o.key === defaultKey));
  const [activeIdx, setActiveIdx] = useState(initialIdx);
  const busyRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      const idx = options.findIndex(o => o.key === (PRESELECT_MAP[currentStatus] || 'in_progress'));
      setActiveIdx(idx >= 0 ? idx : 0);
      busyRef.current = false;
    }
  }, [isOpen, currentStatus, options]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel?.(); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (busyRef.current) return;
        const opt = options[activeIdx];
        if (opt) { busyRef.current = true; onSelect?.(opt.key); }
        return;
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => (i + 1) % options.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => (i - 1 + options.length) % options.length); return; }
      if (/^[1-9]$/.test(e.key)) {
        const n = parseInt(e.key, 10) - 1;
        if (n < options.length) { e.preventDefault(); setActiveIdx(n); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, options, activeIdx, onSelect, onCancel]);

  if (!isOpen) return null;

  const click = (key) => {
    if (busyRef.current) return;
    busyRef.current = true;
    onSelect?.(key);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="relative bg-white dark:bg-surface-card border border-[var(--border-default,#E5E7EB)] dark:border-line rounded-card w-full max-w-md p-6"
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
      >
        <h2 className="text-lg font-semibold text-content mb-1">
          {t('cases.statusAfterSend.title', 'Status nach Antwort setzen')}
        </h2>
        <p className="text-sm text-content-secondary mb-5">
          {t('cases.statusAfterSend.hint', 'Du hast eine Antwort gesendet. Wie geht es jetzt weiter?')}
        </p>

        <div className="flex flex-col gap-2 mb-4">
          {options.map((opt, idx) => {
            const active = idx === activeIdx;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => click(opt.key)}
                onMouseEnter={() => setActiveIdx(idx)}
                className={
                  'w-full text-left flex items-center justify-between px-4 py-3 rounded-card border transition-colors ' +
                  (active
                    ? 'border-[#338edc] bg-[#338edc]/10 text-content'
                    : 'border-[var(--border-default,#E5E7EB)] dark:border-line bg-surface-input text-content hover:border-[#338edc]/60')
                }
                style={active ? { boxShadow: 'inset 0 0 0 1px #338edc' } : undefined}
              >
                <span className="flex items-center gap-3">
                  <span
                    className={
                      'inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-semibold border ' +
                      (active
                        ? 'bg-[#338edc] text-white border-[#338edc]'
                        : 'bg-surface-input text-content-tertiary border-[var(--border-default,#E5E7EB)] dark:border-line')
                    }
                  >
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium">{opt.label}</span>
                </span>
                {active && (
                  <svg className="w-4 h-4 text-[#338edc]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-card border border-[var(--border-default,#E5E7EB)] dark:border-line bg-white dark:bg-surface-input text-sm font-medium text-content hover:bg-surface-hover transition-colors"
          >
            {t('cases.statusAfterSend.cancel', 'Abbrechen')}
          </button>
          <span className="text-[11px] text-content-tertiary">
            {t('cases.statusAfterSend.keyboardHint', 'Enter bestätigt · Esc abbrechen · ↑↓ wechseln · 1–4 direkt')}
          </span>
        </div>
      </div>
    </div>
  );
}
